import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { newId } from "../model/ids";
import { withDecoration } from "../model/projectOps";
import {
  nextStampDecorationName,
  type StampDecoration,
} from "../model/Project";
import { resolveDrawTarget, toOriginalSpace } from "./drawTarget";

/**
 * Places a stamp on a part. Hover shows a ghost following the cursor. A plain
 * click places the stamp at the tool's current rotation. Press-DRAGGING is a
 * separate gesture that places nothing: it aims the tool — the drag direction
 * (angle from the press point to the cursor, shown as a standard guide ray
 * with the shared geometry angle marker) becomes the tool's rotation setting
 * on release, exactly as if the rotation slider had been moved (#920). Stamp
 * with a follow-up click.
 */
export class StampTool extends ToolBase {
  id = "stamp";
  label = "Stamp";
  shortcut = "s";
  cursor = "crosshair";

  private ghost: paper.Item | null = null;
  private ghostKey: string | null = null;
  private lastCursor: { x: number; y: number } | null = null;
  private unsubscribeSettings: (() => void) | null = null;

  /** Active press-drag placement. `center` is the fixed stamp centre; `rotation`
   *  is the live angle (starts at the slider value, follows the drag direction
   *  once the cursor moves past a small dead-zone). `dragged` flips true at
   *  that point — before it, no rotation ray is shown (a 0-length drag has no
   *  angle to report, matching the 0°-suppression the other rays follow). */
  private placing: {
    center: { x: number; y: number };
    rotation: number;
    dragged: boolean;
  } | null = null;

  override onActivate() {
    this.unsubscribeSettings = this.ctx.onSettingsChanged(() => {
      // The rotation slider (etc.) was nudged — rebuild the ghost in place
      // without waiting for the user to move the mouse.
      if (this.lastCursor && !this.placing) this.updateGhost(this.lastCursor);
    });
  }

  override onDeactivate() {
    this.unsubscribeSettings?.();
    this.unsubscribeSettings = null;
    this.clearGhost();
    this.ctx.scene.setGuideRays(null);
    this.placing = null;
    this.lastCursor = null;
  }

  override onHover(_s: PointerSample, p: { x: number; y: number }) {
    this.lastCursor = p;
    this.updateGhost(p);
  }

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    const stampId = this.ctx.settings.activeStampId;
    if (!stampId || !this.ctx.library.get(stampId)) return;
    // Fix the centre here; rotation starts at the slider value and follows the
    // drag direction below. No commit yet — that happens on release. No ray
    // yet either: until a real drag there is no angle to show.
    this.placing = {
      center: { x: p.x, y: p.y },
      rotation: this.ctx.settings.rotation,
      dragged: false,
    };
    this.updateGhost(p, this.placing.rotation);
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    this.lastCursor = p;
    if (this.placing) {
      const c = this.placing.center;
      const dx = p.x - c.x;
      const dy = p.y - c.y;
      const zoom = this.ctx.scene.getPaperScope().view?.zoom ?? 1;
      // Dead-zone before the press counts as an AIM drag rather than a click.
      // A finger or an Apple Pencil wobbles far more than a mouse — on touch
      // AND pen the slop is sized so an accidental few-px smear on an iPad
      // still stamps instead of silently re-aiming the tool.
      const slopPx = s.pointerType === "mouse" ? 6 : 20;
      if (Math.hypot(dx, dy) > slopPx / zoom) {
        // Signed angle from the 0° base, in (-180, 180] — the rotation
        // slider's range, so the adopted value reads back identically there.
        this.placing.rotation = (Math.atan2(dy, dx) * 180) / Math.PI;
        this.placing.dragged = true;
      }
      this.updateGhost(c, this.placing.rotation);
      // The rotation reads as a standard guide ray (#920): centre → cursor with
      // the usual geometry angle marker (horizontal base + arc + degrees),
      // rendered by the shared overlay exactly like the drawing tools' segment
      // rays. Angle only — the drag distance isn't a dimension here. Nothing is
      // shown until a real drag is under way (no 0° stub on a bare press).
      if (this.placing.dragged) {
        // `angleDeg` uses the segment-ray convention: y-up screen angle.
        let rayDeg = (Math.atan2(-dy, dx) * 180) / Math.PI;
        if (rayDeg < 0) rayDeg += 360;
        this.ctx.scene.setGuideRays([
          {
            from: { x: c.x, y: c.y },
            to: { x: p.x, y: p.y },
            angleDeg: rayDeg,
          },
        ]);
      }
      return;
    }
    // Not placing — the ghost tracks the cursor (Scene.render after a commit
    // would otherwise leave the previous ghost orphaned in the rendered tree).
    this.updateGhost(p);
  }

  override onUp(_s: PointerSample, _p: { x: number; y: number }) {
    const placing = this.placing;
    this.placing = null;
    this.ctx.scene.setGuideRays(null);
    if (!placing) return;
    if (placing.dragged) {
      // A drag AIMS the tool instead of stamping: adopt the drag direction as
      // the rotation setting (like moving the slider) and place nothing. The
      // user stamps with a follow-up click. Already signed in the slider's
      // (-180, 180] range.
      this.ctx.settings.rotation = Math.round(placing.rotation);
      this.ctx.notifySettingsChanged();
      return;
    }
    this.commitStamp(placing.center, placing.rotation);
  }

  override onCancel() {
    this.placing = null;
    this.clearGhost();
    this.ctx.scene.setGuideRays(null);
  }

  /** Resolve the target part for a stamp centred at `center` rotated `rotation`
   *  and, if one is found, commit the stamp. Mirrors the pre-#920 placement
   *  logic; only the rotation source changed (drag angle vs. slider). */
  private commitStamp(center: { x: number; y: number }, rotation: number) {
    const stampId = this.ctx.settings.activeStampId;
    if (!stampId) return;
    const meta = this.ctx.library.get(stampId);
    if (!meta) return;
    const scope = this.ctx.scene.getPaperScope();
    const scale = 1;

    // World-space bounds the stamp will occupy: clone the cached symbol
    // prototype, apply the same transform Scene.renderStamp will, read bounds.
    const proto = this.ctx.scene.getStampSymbols().get(stampId);
    if (!proto) return;
    const probe = proto.clone({ insert: false });
    const cx = meta.bbox.x + meta.bbox.w / 2;
    const cy = meta.bbox.y + meta.bbox.h / 2;
    probe.translate(new scope.Point(-cx, -cy));
    probe.scale(scale, scale, new scope.Point(0, 0));
    if (rotation) probe.rotate(rotation, new scope.Point(0, 0));
    probe.translate(new scope.Point(center.x, center.y));
    const stampBounds = probe.bounds.clone();
    probe.remove();

    // Find the part whose outline this stamp lands on (#812).
    const target = resolveDrawTarget(
      this.ctx.history.state,
      { x: center.x, y: center.y },
      scope,
      this.ctx.scene.getSelection().pieceId,
      this.ctx.scene.getPartLayoutView(),
      {
        x: stampBounds.x,
        y: stampBounds.y,
        width: stampBounds.width,
        height: stampBounds.height,
      },
    );
    if (!target) return; // stamp lies entirely outside every part
    const { pieceId, offset } = target;

    const targetPiece = this.ctx.history.state.pieces.find(
      (pc) => pc.id === pieceId,
    );
    const name = targetPiece
      ? nextStampDecorationName(targetPiece, meta.name)
      : meta.name;
    // Store in the part's ORIGINAL space so the spread render translate doesn't
    // double-offset the decoration (#786).
    const dec: StampDecoration = {
      id: newId("dec"),
      kind: "stamp",
      name,
      visible: true,
      depth: meta.deboss ? -0.6 : 0.6,
      stampId,
      position: toOriginalSpace(center, offset),
      rotation,
      scale,
    };
    this.ctx.commit("Stamp", (proj) => withDecoration(proj, pieceId, dec));
  }

  /** Create / refresh / move the floating stamp preview. `rotation` defaults to
   *  the slider value (hover); during a placement drag the caller passes the
   *  live drag angle. */
  private updateGhost(p: { x: number; y: number }, rotation?: number) {
    const stampId = this.ctx.settings.activeStampId;
    if (!stampId) {
      this.clearGhost();
      return;
    }
    const meta = this.ctx.library.get(stampId);
    if (!meta) return;
    const scope = this.ctx.scene.getPaperScope();
    const scale = 1;
    const rot = rotation ?? this.ctx.settings.rotation;
    const key = `${stampId}|${rot}`;

    if (this.ghostKey !== key || !this.ghost || !this.ghost.parent) {
      this.clearGhost();
      const proto = this.ctx.scene.getStampSymbols().get(stampId);
      if (!proto) return;
      const clone = proto.clone({ insert: false });
      const cx = meta.bbox.x + meta.bbox.w / 2;
      const cy = meta.bbox.y + meta.bbox.h / 2;
      clone.translate(new scope.Point(-cx, -cy));
      clone.scale(scale, scale, new scope.Point(0, 0));
      if (rot) clone.rotate(rot, new scope.Point(0, 0));
      clone.opacity = 0.5;
      clone.data = { ephemeral: true };
      scope.project.activeLayer?.addChild(clone);
      this.ghost = clone;
      this.ghostKey = key;
    }

    if (this.ghost) {
      this.ghost.position = new scope.Point(p.x, p.y);
    }
  }

  private clearGhost() {
    this.ghost?.remove();
    this.ghost = null;
    this.ghostKey = null;
  }
}
